import { STALE_AFTER_MS } from '@dabba/contracts';
import type { ConnectionState, MarketDataSource, Tick, Unsubscribe, UserEvent, WsServerMessage } from '@dabba/contracts';

const PING_INTERVAL_MS = 20_000;
const BACKOFF_BASE_MS = 800;
const BACKOFF_MAX_MS = 15_000;
/** Retained-tick ceiling: browsing option chains accumulates hundreds of
 *  contract symbols that would otherwise be cached for the app's lifetime. */
const LAST_CACHE_MAX = 400;

/**
 * The production MarketDataSource: connects to the Dabba ws-gateway.
 * Reconnects with capped exponential backoff, resubscribes its full demand
 * set on reconnect, and reference-counts symbols across screens so shared
 * subscriptions survive one screen unmounting.
 *
 * Honest-price rule: while the transport is down we report 'offline'; once
 * connected, the server's feed frames say whether its upstream feed is live;
 * a symbol whose last tick is older than STALE_AFTER_MS reads as stale via
 * lastTickAt. This source never fabricates a tick.
 */
export class SocketMarketDataSource implements MarketDataSource {
  private ws: WebSocket | null = null;
  private state: ConnectionState = 'offline';
  private refs = new Map<string, number>();
  private last = new Map<string, Tick>();
  private tickListeners = new Set<(t: Tick) => void>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private eventListeners = new Set<(e: UserEvent) => void>();
  private authToken: string | null = null;
  private retry = 0;
  private disposed = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private wsUrl: string) {
    this.connect();
  }

  // ---- MarketDataSource ----

  subscribe(symbols: string[]): void {
    const fresh: string[] = [];
    for (const s of symbols) {
      const n = (this.refs.get(s) ?? 0) + 1;
      this.refs.set(s, n);
      if (n === 1) fresh.push(s);
    }
    if (fresh.length) this.send({ t: 'sub', symbols: fresh });
  }

  unsubscribe(symbols: string[]): void {
    const gone: string[] = [];
    for (const s of symbols) {
      const n = (this.refs.get(s) ?? 0) - 1;
      if (n <= 0) {
        this.refs.delete(s);
        gone.push(s);
      } else {
        this.refs.set(s, n);
      }
    }
    if (gone.length) {
      this.send({ t: 'unsub', symbols: gone });
      this.pruneLast();
    }
  }

  /** Evict zero-ref retained ticks (oldest-inserted first) past the ceiling. */
  private pruneLast(): void {
    if (this.last.size <= LAST_CACHE_MAX) return;
    for (const sym of this.last.keys()) {
      if (this.refs.has(sym)) continue;
      this.last.delete(sym);
      if (this.last.size <= LAST_CACHE_MAX) return;
    }
  }

  onTick(cb: (tick: Tick) => void): Unsubscribe {
    this.tickListeners.add(cb);
    return () => this.tickListeners.delete(cb);
  }

  onConnectionState(cb: (state: ConnectionState) => void): Unsubscribe {
    this.stateListeners.add(cb);
    cb(this.state);
    return () => this.stateListeners.delete(cb);
  }

  /** Personal push events (fills, challenge updates) for the authed user. */
  onEvent(cb: (e: UserEvent) => void): Unsubscribe {
    this.eventListeners.add(cb);
    return () => this.eventListeners.delete(cb);
  }

  /** Attach/detach the user identity. Safe to call on every session change. */
  setAuthToken(token: string | null): void {
    if (token === this.authToken) return;
    this.authToken = token;
    if (token && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'auth', token }));
    }
  }

  connectionState(): ConnectionState {
    return this.state;
  }

  lastTickAt(symbol: string): number | null {
    return this.last.get(symbol)?.at ?? null;
  }

  /** Last retained tick for a symbol (snapshot or live), for instant paint. */
  peek(symbol: string): Tick | null {
    return this.last.get(symbol) ?? null;
  }

  dispose(): void {
    this.disposed = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.silence(this.ws);
      try {
        this.ws.close();
      } catch {
        // already closing
      }
      this.ws = null;
    }
  }

  /** Detach a socket's handlers before replacing it: a late close/error from
   *  the old socket must never fight the live one. */
  private silence(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
  }

  /**
   * Foreground kick: browsers throttle/suspend sockets in background tabs and
   * may never deliver a close event. Called on tab visibility — if the socket
   * is anything but OPEN, drop the backoff and reconnect right now.
   */
  kick(): void {
    if (this.disposed) return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({ t: 'ping' }); // dead-but-OPEN sockets fail here → onclose → reconnect
      return;
    }
    this.retry = 0;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.silence(this.ws);
      try {
        this.ws.close();
      } catch {
        // already closing
      }
      this.ws = null;
    }
    this.connect();
  }

  // ---- transport ----

  private connect(): void {
    if (this.disposed) return;
    // Never stack sockets: if one is already connecting or open, it is the
    // socket — a second connect() (late timer, double kick) must be a no-op.
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) return;
    const ws = new WebSocket(this.wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      // Re-attach identity + resubscribe — server state is per-connection.
      if (this.authToken) ws.send(JSON.stringify({ t: 'auth', token: this.authToken }));
      const symbols = [...this.refs.keys()];
      if (symbols.length) this.send({ t: 'sub', symbols });
      // Transport is up but the feed isn't proven yet: 'reconnecting' until
      // the gateway's feed frame (sent immediately on connect) says live.
      this.setState('reconnecting');
      this.startPing();
    };

    ws.onmessage = (ev) => {
      let msg: WsServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as WsServerMessage;
      } catch {
        return;
      }
      switch (msg.t) {
        case 'tick':
          this.last.set(msg.tick.symbol, msg.tick);
          // Live ticks are proof of life: self-heal a missed/stale feed-state
          // frame. 'closed' is not overridden — that's a market-session fact.
          if (this.state === 'offline' || this.state === 'reconnecting' || this.state === 'stale') this.setState('live');
          for (const cb of this.tickListeners) cb(msg.tick);
          return;
        case 'snapshot':
          for (const tick of msg.ticks) {
            if (!this.last.has(tick.symbol)) {
              this.last.set(tick.symbol, tick);
              for (const cb of this.tickListeners) cb(tick);
            }
          }
          return;
        case 'feed':
          this.setState(msg.state === 'live' ? 'live' : msg.state === 'closed' ? 'closed' : 'reconnecting');
          return;
        case 'event':
          for (const cb of this.eventListeners) cb(msg.event);
          return;
        case 'authed':
        case 'pong':
        case 'error':
          return;
      }
    };

    // Guard on identity: only the CURRENT socket's close may schedule a
    // reconnect — a replaced socket's late close event is just noise.
    ws.onclose = () => {
      if (ws === this.ws) this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        // already closing
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    this.setState('offline');
    if (this.pingTimer) clearInterval(this.pingTimer);
    const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.retry++);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private startPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_INTERVAL_MS);
  }

  private send(msg: { t: 'sub' | 'unsub' | 'ping'; symbols?: string[] }): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      try {
        this.ws.close();
      } catch {
        // already closing
      }
    }
  }

  private setState(s: ConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    for (const cb of this.stateListeners) cb(s);
  }
}

export { STALE_AFTER_MS };
